// Package imports:
import React, { useEffect, useState, useCallback } from "react";
import Bugsnag from "@bugsnag/js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faMinusCircle, faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";
// Component imports:
import Modal from "../../../ui-elements/Modal/Modal";
import Alert from "../../../ui-elements/Alert/Alert";
import Loading from "../../../ui-elements/Loading/Loading";
import Link from "../../../ui-elements/Link/Link";
import Toggle from "../../../ui-elements/Toggle/Toggle";
// Type imports:
import { IndividualMonitorMatch, IndividualMonitorMatches, MatchReview, PepResult, PepWatchResponse } from "../../../types/PepTypes";
import { IKeldanApiResponse } from "../../../types/KeldanTypes";
import { CountryList } from "../../../assets/staticData/CountryList";
import { formatCategories } from "../PepPage";

interface IProps {
  show: boolean;
  close(): void;
  id?: string;
}

const leftKeys: (keyof PepResult)[] = [
  'category',
  'categories',
  'gender',
  'deceased',
  'deceasedDate',
  'firstName',
  'middleName',
  'lastName',
];

const rightKeys: (keyof PepResult)[] = [
  'fullName',
  'images',
  'dateOfBirth',
  'locations',
  'nationalitiesIsoCodes',
];


const renderTitle = (key: keyof PepResult): string => {
  if (key === 'fullName') return 'Full Name';
  if (key === 'dateOfBirth') return 'Date of Birth';
  if (key === 'nationalitiesIsoCodes') return 'Nationalities';
  if (key === 'deceasedDate') return 'Deceased Date';
  if (key === 'firstName') return 'First Name';
  if (key === 'middleName') return 'Middle Name';
  if (key === 'lastName') return 'Last Name';
  return key as string;
};

const renderValue = (value: any, key: keyof PepResult): React.ReactNode => {
  if (key === 'images') {
    if (Array.isArray(value) && value.length > 0) {
      return (
        <div className="pep-image-links">
          {value.map((img: string, i: number) => (
            <div key={`${img}-${i}`}>
              <Link targetBlank splitLines wrapTextForMobile url={img} linkSize="15">{img}</Link>
            </div>
          ))}
        </div>
      );
    }
    return '-';
  }
  if (key === 'locations') {
    if (Array.isArray(value) && value.length > 0) {
      return value.map((loc: any, i: number) => (
        <div key={i}>
          {loc.address?.length > 0 ? loc.address : CountryList.find(c => c.value === loc.countryCode)?.label ?? '-'}
        </div>
      ));
    }
    return '-';
  }
  if (key === 'nationalitiesIsoCodes') {
    return Array.isArray(value) ? value.map((code: string) => CountryList.find(c => c.value === code)?.label ?? code).join(', ') : '-';
  }
  if (key === 'categories') return formatCategories(value);
  if (key === 'deceased') return value ? value.toString() : '-';
  return value === '' || value === null || value === undefined ? '-' : value;
};

type ReviewStatus = 'true-positive' | 'false-positive';

type ReviewFormState = {
  open: boolean;
  submitting: boolean;
  success: boolean;
  successStatus?: ReviewStatus;
  error?: string;
};

const defaultReviewForm = (): ReviewFormState => ({
  open: false,
  submitting: false,
  success: false,
  successStatus: undefined,
  error: undefined,
});

const PepWatchSanctionsConnectionModal: React.FC<IProps> = ({ show, close, id }) => {
  const [pages, setPages] = useState<IndividualMonitorMatch[][]>([]);
  const [nextTokens, setNextTokens] = useState<(string | undefined)[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<Error | undefined>(undefined);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [profileCache, setProfileCache] = useState<Record<string, PepResult | Error | 'loading'>>({});
  const [reviewForms, setReviewForms] = useState<Record<string, ReviewFormState>>({});

  const fetchPage = useCallback(async (personId: string, nextToken?: string): Promise<IndividualMonitorMatches> => {
    let url = `/pep/GetIndividualMatches?personId=${personId}`;
    if (nextToken) url += `&nextToken=${encodeURIComponent(nextToken)}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Error: ${response.status}`);
    const responseBody: IKeldanApiResponse<PepWatchResponse<IndividualMonitorMatches>> = await response.json();
    if (responseBody.success && responseBody.result) {
      return responseBody.result.data;
    }
    throw new Error(responseBody.message ?? "Ekki tókst að sækja gögn");
  }, []);

  useEffect(() => {
    if (!id) return;

    const init = async () => {
      try {
        setLoading(true);
        setFetchError(undefined);
        setPages([]);
        setNextTokens([]);
        setCurrentPageIndex(0);
        setExpandedRows(new Set());
        setProfileCache({});
        setReviewForms({});

        const data = await fetchPage(id);
        setPages([data.matches ?? []]);
        setNextTokens([data.nextToken]);
      } catch (e) {
        if (e instanceof Error) {
          Bugsnag.notify(e);
          setFetchError(new Error("Ekki tókst að sækja gögn um alþjóðlega válista. Vinsamlegast reyndu aftur síðar."));
        }
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [id, fetchPage]);

  const goToPage = useCallback(async (targetIndex: number) => {
    if (!id) return;

    if (targetIndex < pages.length) {
      setCurrentPageIndex(targetIndex);
      setExpandedRows(new Set());
      return;
    }

    const tokenForNextPage = nextTokens[targetIndex - 1];
    if (!tokenForNextPage) return;

    try {
      setLoading(true);
      const data = await fetchPage(id, tokenForNextPage);
      setPages(prev => [...prev, data.matches ?? []]);
      setNextTokens(prev => [...prev, data.nextToken]);
      setCurrentPageIndex(targetIndex);
      setExpandedRows(new Set());
    } catch (e) {
      if (e instanceof Error) {
        Bugsnag.notify(e);
        setFetchError(new Error("Ekki tókst að sækja næstu síðu. Vinsamlegast reyndu aftur."));
      }
    } finally {
      setLoading(false);
    }
  }, [id, pages.length, nextTokens, fetchPage]);

  const toggleRow = async (idx: number, match: IndividualMonitorMatch) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) { next.delete(idx); } else { next.add(idx); }
      return next;
    });

    const resourceId = match.resourceId;
    if (!resourceId) return;

    if (!profileCache[resourceId]) {
      setProfileCache(prev => ({ ...prev, [resourceId]: 'loading' as const }));
      try {
        const response = await fetch(`/pep/GetIndividualProfile?resourceId=${resourceId}`);
        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const responseBody: IKeldanApiResponse<PepWatchResponse<PepResult>> = await response.json();
        if (responseBody.success && responseBody.result) {
          setProfileCache(prev => ({ ...prev, [resourceId]: responseBody.result!.data }));
        } else {
          throw new Error(responseBody.message ?? "Ekki tókst að sækja upplýsingar");
        }
      } catch (e) {
        const err = e instanceof Error ? e : new Error("Óþekkt villa");
        setProfileCache(prev => ({ ...prev, [resourceId]: err }));
      }
    }
  };

  const updateReviewForm = (qrCode: string, patch: Partial<ReviewFormState>) => {
    setReviewForms(prev => ({
      ...prev,
      [qrCode]: { ...(prev[qrCode] ?? defaultReviewForm()), ...patch },
    }));
  };

  const submitReview = async (match: IndividualMonitorMatch, status: ReviewStatus) => {
    const { qrCode } = match;
    if (!qrCode) return;

    updateReviewForm(qrCode, { submitting: true, error: undefined });

    try {
      const body: MatchReview[] = [{ qrCode, status }];

      const response = await fetch(`/pep/ReviewMatch?personId=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const responseBody: IKeldanApiResponse<unknown> = await response.json();

      if (responseBody.success) {
        updateReviewForm(qrCode, { submitting: false, success: true, successStatus: status, open: false });
      } else {
        throw new Error(responseBody.message ?? "Ekki tókst að senda umsögn");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Óþekkt villa";
      if (e instanceof Error) Bugsnag.notify(e);
      updateReviewForm(qrCode, { submitting: false, error: msg });
    }
  };

  const getEffectiveStatus = (match: IndividualMonitorMatch): string | undefined => {
    const form = reviewForms[match.qrCode ?? ''];
    if (form?.success && form.successStatus) return form.successStatus;
    return match.currentStatus;
  };

  const renderReviewCell = (match: IndividualMonitorMatch) => {
    const { qrCode } = match;
    const form = reviewForms[qrCode ?? ''] ?? defaultReviewForm();
    const effectiveStatus = getEffectiveStatus(match);
    const isFalsePositive = effectiveStatus === 'false-positive';

    return (
      <div className="pep-review-cell" onClick={(e) => e.stopPropagation()}>
        <Toggle
          toggled={isFalsePositive}
          disabled={!qrCode || form.submitting}
          label="Hafna"
          onToggle={(toggled) => {
            if (qrCode) {
              submitReview(match, toggled ? 'false-positive' : 'true-positive');
            }
          }}
        />
        {form.error && <span className="pep-review-form__error">{form.error}</span>}
      </div>
    );
  };

  const renderProfileColumn = (profile: PepResult, keys: (keyof PepResult)[], extraRows?: JSX.Element) => (
    <div className="profile-column">
      {extraRows}
      {keys.map((key, i) => {
        const longValue = key === 'locations' || key === 'images' || key === 'categories';
        return (
          <div className="item_container" key={i} style={{ padding: '16px 5px' }}>
            <span className={cx('item_key', { 'long_value': longValue })}>
              {renderTitle(key)}
            </span>
            <span className="item_value">
              {renderValue(profile[key], key)}
            </span>
          </div>
        );
      })}
    </div>
  );

  const renderProfile = (match: IndividualMonitorMatch) => {
    const { resourceId, score } = match;
    if (!resourceId) return null;
    const profile = profileCache[resourceId];
    if (!profile || profile === 'loading') return <Loading />;
    if (profile instanceof Error) return <Alert type="error" headText={profile.message} />;

    return (
      <div className="KCL_foreignPepModal pep-profile-split">
        {renderProfileColumn(profile, leftKeys,
          <div className="item_container" style={{ padding: '16px 5px' }}>
            <span className="item_key">Match rate</span>
            <span className="item_value">{score ?? '-'}</span>
          </div>
        )}
        {renderProfileColumn(profile, rightKeys)}
      </div>
    );
  };

  const currentMatches = pages[currentPageIndex] ?? [];
  const hasPrev = currentPageIndex > 0;
  const hasNext = currentPageIndex < pages.length - 1 || !!nextTokens[currentPageIndex];
  const totalFetched = pages.reduce((sum, p) => sum + p.length, 0);

  const modalBody = () => {
    if (loading && pages.length === 0) return <Loading />;
    if (fetchError) return <Alert type="error" headText={fetchError.message} />;
    if (pages.length > 0 && currentMatches.length === 0 && !hasNext) return <Alert type="alert" headText="Engar niðurstöður fundust" />;

    return (
      <div className="KCL_foreignPepModal-multi">
        <table className="pep-results-table">
          <thead>
            <tr>
              <th></th>
              <th>Nafn</th>
              <th>Match Rate</th>
              <th>Lönd</th>
              <th>Fæðingardagur</th>
              <th>Hafna niðurstöðu</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? (
                <tr>
                  <td colSpan={7}><Loading /></td>
                </tr>
              )
              : currentMatches.map((match, idx) => {
                const isOpen = expandedRows.has(idx);
                const countries = match.countries?.join(', ') || '-';
                const dob = match.datesOfBirth?.join(', ') || '-';
                return (
                  <React.Fragment key={idx}>
                    <tr
                      className={cx({ 'is-open': isOpen })}
                      onClick={() => toggleRow(idx, match)}
                      role="button"
                      aria-expanded={isOpen}
                      aria-controls={`match-row-details-${idx}`}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleRow(idx, match);
                        }
                      }}
                    >
                      <td>
                        <button type="button" className="pep-row-toggle" aria-label={isOpen ? 'Loka' : 'Opna'}>
                          <FontAwesomeIcon icon={isOpen ? faMinusCircle : faPlusCircle} />
                        </button>
                      </td>
                      <td>{match.name || '-'}</td>
                      <td>{match.score ?? '-'}</td>
                      <td>{countries}</td>
                      <td>{dob}</td>
                      <td>{renderReviewCell(match)}</td>
                      <td>
                        {match.resourceId && (
                          <Link
                            targetBlank
                            linkSize="15"
                            url={`/pep/GetIndividualProfilePdf?resourceId=${match.resourceId}`}
                            icon="pdf"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Sækja skýrslu
                          </Link>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr id={`match-row-details-${idx}`} className="pep-details-row">
                        <td colSpan={8}>
                          {renderProfile(match)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            }
          </tbody>
        </table>

        {(hasPrev || hasNext) && (
          <div className="pep-pagination">
            <button
              type="button"
              className="pep-pagination__btn"
              disabled={!hasPrev || loading}
              onClick={() => goToPage(currentPageIndex - 1)}
              aria-label="Fyrri síða"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="pep-pagination__label">
              {`Síða ${currentPageIndex + 1}${pages.length > 1 ? ` / ${pages.length}${hasNext && currentPageIndex === pages.length - 1 ? '+' : ''}` : hasNext ? '+' : ''}`}
              {` · ${totalFetched} niðurstöður`}
            </span>
            <button
              type="button"
              className="pep-pagination__btn"
              disabled={!hasNext || loading}
              onClick={() => goToPage(currentPageIndex + 1)}
              aria-label="Næsta síða"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      className="pep-watch-modal"
      show={show}
      onHide={close}
      hasCloseButton
      header={
        pages.length === 0
          ? 'Alþjóðlegir válistar'
          : `Alþjóðlegir válistar (${totalFetched}${hasNext ? '+' : ''})`
      }
      body={modalBody()}
    />
  );
};

export default PepWatchSanctionsConnectionModal;
